import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import CustomTooltip, { POSITIONS } from '@components/CustomTooltip';
import DeleteConfirmationModal from '@components/DeleteConfirmationModal';
import NoItemFound from '@components/NoItemFound';
import AddItemModal from '@components/rangePlan/AddItemModal';
import { FabricCategory, FitTypePopup, GSMPopup, PriceRange } from '@components/rangePlan/CommonPopups';
import CompositionPopup from '@components/rangePlan/CompositionPopup';
import {
	calculateStylesCount,
	checkValidId,
	ENTITY_TYPE,
	getRangeValue,
	getReducedPrice,
	groupByField,
	isAllowedToDelete,
	isCellIdMatched,
	RANGE_PLAN_TABLE_HEADER,
	UPDATE_ENTITY_TYPE
} from '@components/rangePlan/rangePlanUtils';
import ScrollButtons from '@components/ScrollButtons';

import { clearGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { setActiveFavouritePageTab } from '@store/favourite/actions';

import {
	downloadRangePlan,
	fetchRangePlanTableData,
	removeRangePlanItem,
	toggleTarget,
	updateRangePlaningTarget
} from '@services/rangePlanService';

import { printApiError } from '@utils/Util';

export const TableActionButton = ({ onActionClick, item, isAllowedToDelete }) => {
	return (
		<div className="edit-items">
			<div className="option">
				<div className="dropdown">
					<button
						className="btn btn-default p-0 dropdown-toggle"
						type="button"
						id={`menu_${item?.id}`}
						data-toggle="dropdown"
						aria-expanded="true"
						disabled={!isAllowedToDelete(item)}
					>
						<CommonImage src="/icons/more_icon.svg" alt="more_icon" isStatic={true} />
					</button>

					<ul
						className="dropdown-menu dropdown-menu-right shadow-lg"
						role="menu"
						aria-labelledby={`menu_${item?.id}`}
						x-placement="bottom-end"
						x-out-of-boundaries
					>
						<li role="presentation" onClick={() => onActionClick(item)}>
							<span className="red">Delete</span>
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
};

export const ToggleTooltip = ({ items, isArray = false, property = 'name' }) => {
	const tagDetails = isArray ? items : Object.keys(items || {});
	return (
		<div class="tag-tooltip">
			<div class="all-status d-flex flex-column">
				{tagDetails?.slice(2)?.map((item, index) => (
					<span class="status radius" key={`tag_${index}`}>
						{isArray ? item?.[property] : `${item} (${items[item]})`}
					</span>
				))}
			</div>
		</div>
	);
};

const RangePlanTable = ({
	entityType = ENTITY_TYPE.FAVOURITE_FOLDER,
	setActiveTab,
	brandName = '',
	brandId = null
}) => {
	const [selectedTargetPriceRangeId, setSelectedTargetPriceRangeId] = useState(null);
	const [selectedTargetGSMId, setSelectedTargetGSMId] = useState(null);
	const [selectedTargetFitTypeId, setSelectedTargetFitTypeId] = useState(null);
	const [selectedTargetCategoryId, setSelectedTargetCategoryId] = useState(null);
	const [selectedTargetCompositionRowId, setSelectedTargetCompositionRowId] = useState(null);
	const [targetQuantityRowId, setTargetQuantityRowId] = useState(null);
	const [isShowTarget, setIsShowTarget] = useState(false);
	const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
	const dispatch = useDispatch();
	const [itemToDelete, setItemToDelete] = useState(null);
	const [hoverItemId, setHoverItemId] = useState(null);
	const [hoverColumnName, setHoverColumnName] = useState('');
	const { id } = useParams();
	const [targetCostedStyles, setTargetCostedStyles] = useState(null);
	const [rangePlanList, setRangePlanList] = useState([]);
	const [isShowAddItemModal, setIsShowAddItemModal] = useState(false);
	const scrollButtonRef = useRef(null);
	const [targetQuantity, setTargetQuantity] = useState('');

	// Table Header target, techpack, sampleDeveloped, costed
	const calculateRangePlanTableData = () => {
		let targetStyles = 0;
		let achievedStyles = 0;
		let inRangeCostedStyles = 0;
		let totalCostedStyles = 0;

		rangePlanList?.forEach((item) => {
			targetStyles += Number(item?.targetQuantity || 0);
			achievedStyles += Number(item?.achievedQuantity || 0);
			inRangeCostedStyles += Number(item?.achievedBasePriceQuantity || 0);
		});
		setTargetCostedStyles({
			targetStyles,
			achievedStyles,
			inRangeCostedStyles,
			totalCostedStyles
		});
	};

	const getRangePlanTableData = async () => {
		try {
			dispatch(openLoader());
			const response = await fetchRangePlanTableData(id, entityType);
			const data = response?.planningItemList?.map((item, index) => {
				const baseItem = {
					...item,
					id: item?.id || `INDEX_${index}`
				};

				if (item?.isNewParentCategory) {
					baseItem.parentCategoryId = `PARENT_${index}`;
				}
				if (item?.isNewFabricType) {
					baseItem.fabricTypeId = `FABRIC_TYPE_${index}`;
				}
				if (item?.isNewProductGroup) {
					baseItem.mainProductGroupId = `PRODUCT_GROUP_${index}`;
				}

				return baseItem;
			});

			setRangePlanList(data);
			setIsShowTarget(response?.showTargetInfo);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		getRangePlanTableData();
	}, [id, entityType]);

	useEffect(() => {
		calculateRangePlanTableData();
	}, [rangePlanList]);

	const showConfirmationModal = (selectedItem) => {
		setIsShowConfirmationModal(true);
		setItemToDelete(selectedItem);
	};

	const handleTargetToggle = async () => {
		setIsShowTarget(!isShowTarget);
		try {
			dispatch(openLoader());
			await toggleTarget(id, entityType, !isShowTarget);
			getRangePlanTableData();
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleRemoveRow = async () => {
		if (isAllowedToDelete(itemToDelete))
			try {
				dispatch(openLoader());
				const response = await removeRangePlanItem(itemToDelete?.id);
				if (response?.success) {
					setItemToDelete(null);
					setIsShowConfirmationModal(false);
					getRangePlanTableData();
				}
			} catch (error) {
				printApiError(error);
			} finally {
				dispatch(closeLoader());
			}
	};

	const createFilterObject = (fieldName, fieldValue, fieldKey) => {
		return {
			name: fieldName,
			type: 'text',
			key: encodeURIComponent(fieldKey),
			value: fieldValue,
			identifier: fieldName,
			isShowChip: true
		};
	};

	const handleCategoryClick = (item) => {
		dispatch(clearGlobalFilter());
		if (entityType === ENTITY_TYPE.FAVOURITE_FOLDER) {
			dispatch(setActiveFavouritePageTab({ code: 'DESIGN', title: 'Design' }));
		} else if (entityType === ENTITY_TYPE.COLLECTION) {
			setActiveTab && setActiveTab('designs');
		}

		const categoryFilter = createFilterObject(
			entityType === ENTITY_TYPE.FAVOURITE_FOLDER ? 'favouriteCategory' : 'collectionCategory',
			item?.categoryResponse?.name,
			item?.categoryResponse?.id
		);
		const productGroupFilter = createFilterObject(
			'productGroupIdList',
			item?.productGroupResponse?.name,
			item?.productGroupResponse?.id
		);
		const constructionFilter = createFilterObject(
			'fabricTypeList',
			item?.fabricType?.name,
			item?.fabricType?.constant || ''
		);

		dispatch(updateTextItemInGlobalFilter(categoryFilter));
		dispatch(updateTextItemInGlobalFilter(productGroupFilter));
		dispatch(updateTextItemInGlobalFilter(constructionFilter));
	};

	const handleUpdateTarget = async (requestBody, onSuccess) => {
		try {
			dispatch(openLoader());
			const response = await updateRangePlaningTarget(requestBody);
			if (response?.success) {
				getRangePlanTableData();
				onSuccess && onSuccess();
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	// Update Target Quantity
	const handleUpdateTargetQuantity = (value, item) => {
		const body = {
			id: checkValidId(item),
			entityId: id,
			entityType: entityType,
			fieldName: UPDATE_ENTITY_TYPE.QUANTITY,
			targetQuantity: value,
			productGroupId: item?.productGroupResponse?.id,
			categoryId: item?.categoryResponse?.id,
			fabricType: item?.fabricType?.constant
		};

		if (value) {
			handleUpdateTarget(body, setTargetQuantityRowId(null));
		} else {
			setTargetQuantityRowId(null);
		}
	};

	const handleDownload = async () => {
		try {
			dispatch(openLoader());
			await downloadRangePlan(id, entityType);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleHover = (itemId, columnName) => {
		setHoverItemId(itemId);
		setHoverColumnName(columnName);
	};

	const renderAchievedTags = (tagsDetails, columnName, itemId, property = 'name') => {
		return (
			<>
				{Object.keys(tagsDetails || {})
					?.slice(0, 2)
					?.map(
						(composition) =>
							composition && (
								<span className="status gray-1 mr-1 mb-1" key={composition}>
									{composition} {`(${tagsDetails[composition]})`}
								</span>
							)
					)}
				{Object.keys(tagsDetails || {})?.length > 2 && (
					<span
						className="status gray-1  mr-1 mb-1 cursor-pointer count"
						onMouseEnter={() => handleHover(itemId, columnName)}
						onMouseLeave={() => handleHover(null, null)}
						key={itemId}
					>
						{Object.keys(tagsDetails || {})?.slice(2)?.length + '+'}

						{hoverItemId === itemId && hoverColumnName === columnName && (
							<ToggleTooltip items={tagsDetails} property={property} />
						)}
					</span>
				)}
			</>
		);
	};

	const renderTargetTags = (tagsDetails = [], itemId, columnName, property = 'name') => {
		return (
			<>
				{tagsDetails?.slice(0, 2)?.map((item) => (
					<span className="status mr-1 mb-1 gray-1 cursor-pointer" key={item?.id}>
						{columnName === 'composition'
							? `${item?.persantage}% ${item?.fiberName}`
							: item?.[property]}
					</span>
				))}
				{tagsDetails?.length > 2 && (
					<span
						className="status mr-1 mb-1 gray-1 cursor-pointer"
						onMouseEnter={() => handleHover(itemId, columnName)}
						onMouseLeave={() => handleHover(null, null)}
					>
						{tagsDetails?.length - 2 + '+'}
						{hoverItemId === itemId && hoverColumnName === columnName && (
							<ToggleTooltip items={tagsDetails} isArray={true} property={property} />
						)}
					</span>
				)}
			</>
		);
	};

	const renderTargetComposition = (composition, itemId, columnName) => {
		return (
			<>
				{composition?.slice(0, 2)?.map((items) => (
					<span className="status mr-1 mb-1 gray-1 cursor-pointer" key={items[0]?.id}>
						{items?.map((item, index) => (
							<React.Fragment key={item?.id}>
								{item?.percentage}% {item?.fiberName}
								{index < items.length - 1 && ' '}
							</React.Fragment>
						))}
					</span>
				))}
				{composition?.length > 2 && (
					<span
						className="status mr-1 mb-1 gray-1 cursor-pointer"
						onMouseEnter={() => handleHover(itemId, columnName)}
						onMouseLeave={() => handleHover(null, null)}
					>
						{composition?.length - 2 + '+'}
					</span>
				)}
				{hoverItemId === itemId && hoverColumnName === columnName && (
					<React.Fragment>
						<div className="tag-tooltip">
							<div className="all-status d-flex flex-column">
								{composition?.slice(2)?.map((items) => (
									<span className="status radius" key={items[0]?.id}>
										{items?.map((item, index) => (
											<React.Fragment key={item?.id}>
												{item?.percentage}% {item?.fiberName}
												{index < items.length - 1 && ' '}
											</React.Fragment>
										))}
									</span>
								))}
							</div>
						</div>
					</React.Fragment>
				)}
			</>
		);
	};

	return (
		<React.Fragment>
			<div className="top-header range-plan-top-header">
				<div className="filter-container-top filter-container-top-v2 px-0">
					<div className="filters">
						<div className="chip-container">
							<div className="range-list-and-target-toggle d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center align-self-center">
									<ul className="common-list-component">
										<li>
											Target:&nbsp;
											<span className="gray_dark_01 medium-14">
												{targetCostedStyles?.targetStyles}&nbsp;styles
											</span>
										</li>
										<li>
											Techpack:&nbsp;
											<span className="gray_dark_01 medium-14">
												&nbsp;{targetCostedStyles?.achievedStyles}&nbsp;styles
											</span>
										</li>
										<li>
											Sample Developed:&nbsp;
											<span className="gray_dark_01 medium-14">
												&nbsp;
												{calculateStylesCount(rangePlanList)?.sampleDevelopedStyles}
												&nbsp;styles
											</span>
										</li>
										<li>
											Costed:&nbsp;
											<span className="gray_dark_01 medium-14">
												{calculateStylesCount(rangePlanList)?.costedStyles}
												&nbsp;styles
											</span>
										</li>
										<li>
											In range:&nbsp;
											<span className="gray_dark_01 medium-14">
												{calculateStylesCount(rangePlanList)?.inRangeStyles}
												&nbsp;styles
											</span>
										</li>
										<li>
											Out of range:&nbsp;
											<span className="gray_dark_01 medium-14">
												{calculateStylesCount(rangePlanList)?.outOfRangeStyles}
												&nbsp;styles
											</span>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<div className="filter-actions favourite-filter-actions">
						<div className="add-new-design">
							<button
								className="button text size32 px-0"
								disabled={!isShowTarget}
								onClick={() => setIsShowAddItemModal(true)}
							>
								<CommonImage src="/icons/add-round.svg" alt="add" isStatic={true} />
								<span className="ml-1">Add Item</span>
							</button>
						</div>
						<div className="target-and-download-btn d-flex align-items-center">
							<div className="ml-2 pl-1 d-flex">
								<p className="gray_dark_02 regular-14 pr-2">Target</p>
								<CommonImage
									onClick={handleTargetToggle}
									className="cursor-pointer"
									src={`${isShowTarget ? '/icons/toggle-on.svg' : '/icons/toggle-off.svg'}`}
									alt="toggle"
									isStatic={true}
								/>
							</div>
							<button className="button border-0 outline mx-2 px-0" onClick={handleDownload}>
								<CommonImage
									src="/icons/download-dark-20.svg"
									alt="download"
									isStatic={true}
								/>
								<span className="ml-1 regular-14">Download</span>
							</button>
							<div>
								<ScrollButtons
									className="scroll-table-buttons position-static ml-2"
									scrollButtonRef={scrollButtonRef}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			{rangePlanList?.length > 0 ? (
				<div className="range-planing-table">
					<table
						className="table with-border hover scroll-x-label scroll-y-label"
						ref={scrollButtonRef}
					>
						<thead>
							<tr>
								{RANGE_PLAN_TABLE_HEADER(isShowTarget).map((header) => (
									<th
										key={header?.title}
										rowSpan={header?.rowspan}
										colSpan={header?.colspan}
										className={`${header?.className}`}
									>
										{header?.title}
									</th>
								))}
							</tr>
							<tr>
								{isShowTarget && <th className="target-column quantity-target">Target</th>}
								<th className="achieved-column quantity-achieved">Techpack</th>
								{isShowTarget && (
									<th className="achieved-column quantity-achieved">Sample Dev.</th>
								)}
								{isShowTarget && <th className="target-column price-achieved">Target</th>}
								<th className="achieved-column price-achieved">
									<div className="d-flex align-items-center">
										<p>Achieved</p>
										<div className="ml-1 cursor-pointer">
											<CustomTooltip
												text={'In range/Total Costed (Price Range)'}
												position={POSITIONS.TOP}
												arrowCenter={true}
												className="custom-tooltip"
											>
												<CommonImage
													src="/icons/info.svg"
													alt="info"
													isStatic={true}
												/>
											</CustomTooltip>
										</div>
									</div>
								</th>
								{isShowTarget && <th className="target-column gsm-target">Target</th>}
								<th className="achieved-column gsm-achieved">Achieved</th>
								{isShowTarget && <th className="target-column fit-target">Target</th>}
								<th className="achieved-column fit-achieved">Achieved</th>
								{isShowTarget && <th className="target-column fabric-target">Target</th>}
								<th className="achieved-column fabric-achieved">Achieved</th>
								{isShowTarget && <th className="target-column composition-target">Target</th>}
								<th className="achieved-column composition-achieved">Achieved</th>
							</tr>
						</thead>

						{/* Table Body  */}
						<tbody>
							{rangePlanList.map((item) => (
								<tr key={`ID_${item?.id}`}>
									{/* Under Market Column */}
									{item?.isNewProductGroup && (
										<td rowSpan={item?.noOfSameProductGroup} className="market-column">
											<p className="gray_dark_01">
												{item?.productGroupResponse?.name} (
												{
													groupByField('productGroup', rangePlanList)?.[
														item?.mainProductGroupId
													]
												}
												)
											</p>
										</td>
									)}
									{/* Under Fabric Column */}
									{item?.isNewFabricType && (
										<td rowSpan={item?.noOfSameFabricType} className="fabric-type-column">
											<p className="gray_dark_01">
												{item?.fabricType?.name} (
												{
													groupByField('fabricType', rangePlanList)?.[
														item?.fabricTypeId
													]
												}
												)
											</p>
										</td>
									)}
									{/* Under TBO Column */}
									{item?.isNewParentCategory && (
										<td rowSpan={item?.noOfSameParentCategory} className="tbo-column">
											{item?.parentCategory?.name} (
											{
												groupByField('parentCategory', rangePlanList)?.[
													item?.parentCategoryId
												]
											}
											)
										</td>
									)}
									{/* Under Category Column */}
									<td
										rowSpan={1}
										className="category-column"
										onClick={() => handleCategoryClick(item)}
									>
										<p className="cursor-pointer text-underline">
											{item?.categoryResponse?.name}
										</p>
									</td>
									{/* Under Quantity > Target Column */}
									{isShowTarget && (
										<td
											className="target-and-achieved-column cursor-pointer"
											onClick={() => setTargetQuantityRowId(item?.id)}
										>
											{isCellIdMatched(targetQuantityRowId, item) ? (
												<span>
													<input
														type="number"
														min={1}
														step={1}
														value={targetQuantity}
														onChange={(e) => setTargetQuantity(e.target.value)}
														name={`targetQuantity-${item?.id}`}
														autoFocus
														onFocus={() =>
															setTargetQuantity(item?.targetQuantity)
														}
														onBlur={(e) => {
															handleUpdateTargetQuantity(
																Math.floor(e.target.value),
																item
															);
															setTargetQuantity('');
														}}
													/>
												</span>
											) : (
												<p>{item?.targetQuantity}</p>
											)}
										</td>
									)}
									{/* Under Quantity > Achieved */}
									<td>{item?.achievedQuantity}</td>
									{/* Under Quantity > Sample Development */}
									{isShowTarget && (
										<td>
											<p>{item?.sampleDevelopedQuantity}</p>
										</td>
									)}
									{/* Under Price Range > Target */}
									{isShowTarget && (
										<td
											onClick={() => setSelectedTargetPriceRangeId(item?.id)}
											className="price-range-column cursor-pointer"
										>
											{isCellIdMatched(selectedTargetPriceRangeId, item) ? (
												<div>
													<p className="gray_dark_01">
														{getRangeValue(
															item?.targetMinPrice || item?.brandMinPrice,
															item?.targetMaxPrice || item?.brandMaxPrice,
															true
														)}
													</p>
													<PriceRange
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
														setSelectedTargetPriceRangeId={
															setSelectedTargetPriceRangeId
														}
														brandName={brandName}
													/>
												</div>
											) : (
												<p className="gray_dark_01">
													{getRangeValue(
														item?.targetMinPrice || item?.brandMinPrice,
														item?.targetMaxPrice ||
															getReducedPrice(item?.brandMaxPrice),
														true
													)}
												</p>
											)}
										</td>
									)}

									{/* Under Price Range > Achived */}
									<td className="price-range-column achieved">
										<p className="gray_dark_01">
											{item?.achievedMinPrice || item?.achievedMaxPrice ? (
												<span>
													{item?.achievedBasePriceQuantity || 0}/
													{item?.totalCostedQuantity || 0}
												</span>
											) : (
												''
											)}{' '}
											{item?.achievedMinPrice || item?.achievedMaxPrice ? (
												<span>
													(
													{getRangeValue(
														item?.achievedMinPrice,
														item?.achievedMaxPrice,
														true
													)}
													)
												</span>
											) : (
												''
											)}
										</p>
									</td>
									{/* Under GSM Range > Target  */}
									{isShowTarget && (
										<td
											onClick={() => setSelectedTargetGSMId(item?.id)}
											className="price-range-column cursor-pointer"
										>
											{isCellIdMatched(selectedTargetGSMId, item) ? (
												<div>
													<p>
														{getRangeValue(
															item?.targetMinGsm,
															item?.targetMaxGsm
														)}
													</p>
													<GSMPopup
														setSelectedTargetGSMId={setSelectedTargetGSMId}
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
													/>
												</div>
											) : (
												<p>{getRangeValue(item?.targetMinGsm, item?.targetMaxGsm)}</p>
											)}
										</td>
									)}
									{/* Under GSM Range > Achived  */}
									<td className="price-range-column">
										<p>{getRangeValue(item?.achievedMinGsm, item?.achievedMaxGsm)}</p>
									</td>
									{/* Under Fit Type > Target */}
									{isShowTarget && (
										<td
											className="fit-type-column cursor-pointer"
											onClick={() => setSelectedTargetFitTypeId(item?.id)}
										>
											{isCellIdMatched(selectedTargetFitTypeId, item) ? (
												<div>
													{renderTargetTags(
														item?.targetFitTypes || [],
														item?.id,
														'fitType',
														'text'
													)}
													<FitTypePopup
														setSelectedTargetFitTypeId={
															setSelectedTargetFitTypeId
														}
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
													/>
												</div>
											) : (
												renderTargetTags(
													item?.targetFitTypes || [],
													item?.id,
													'fitType',
													'text'
												)
											)}
										</td>
									)}
									{/* Under Fit Type > Achived */}
									<td className="fit-type-column">
										{renderAchievedTags(
											item?.achievedFitTypeCountMap,
											item?.id,
											'fitType',
											'text'
										)}
									</td>
									{/* Under Fabric Category > Target */}
									{isShowTarget && (
										<td
											onClick={() => setSelectedTargetCategoryId(item?.id)}
											className="fit-type-column fabric-category-column cursor-pointer"
										>
											{isCellIdMatched(selectedTargetCategoryId, item) ? (
												<div>
													{renderTargetTags(
														item?.targetFabricConstructions || [],
														item?.id,
														'fabricCategory',
														'name'
													)}
													<FabricCategory
														setSelectedTargetCategoryId={
															setSelectedTargetCategoryId
														}
														onUpdateTarget={handleUpdateTarget}
														item={item}
														id={id}
														entityType={entityType}
													/>
												</div>
											) : (
												renderTargetTags(
													item?.targetFabricConstructions || [],
													item?.id,
													'fabricCategory',
													'name'
												)
											)}
										</td>
									)}
									{/* Under Fabric Category > Achived */}
									<td className="fit-type-column fabric-category-column">
										<div className="status-and-count d-flex">
											{renderAchievedTags(
												item?.achievedConstructionCountMap,
												item?.id,
												'fabricCategory',
												'name'
											)}
										</div>
									</td>
									{/* Under Composition > Target */}
									{isShowTarget && (
										<td
											className="compositon-target-column cursor-pointer"
											onClick={() => setSelectedTargetCompositionRowId(item?.id)}
										>
											{item?.id === selectedTargetCompositionRowId ? (
												<div>
													{renderTargetComposition(
														item?.targetFabricCompositions,
														item?.id,
														'composition'
													)}
													<CompositionPopup
														id={id}
														item={item}
														setSelectedTargetCompositionRowId={
															setSelectedTargetCompositionRowId
														}
														onUpdateTarget={handleUpdateTarget}
														entityType={entityType}
														handleClosePopup={() => {
															setSelectedTargetCompositionRowId(null);
														}}
													/>
												</div>
											) : (
												renderTargetComposition(
													item?.targetFabricCompositions,
													item?.id,
													'composition'
												)
											)}
										</td>
									)}
									{/* Under Composition > Achived */}
									<td className="compositon-target-column">
										<div className="status-and-count d-flex">
											{renderAchievedTags(
												item?.achievedCompositionDetailsCountMap,
												item?.id,
												'composition',
												'name'
											)}
										</div>
									</td>
									{/* Under Three Dot Action Button  */}
									<td
										className={`${
											isAllowedToDelete(item)
												? 'action-column '
												: 'action-column ineligible-bg'
										}`}
									>
										<TableActionButton
											item={item}
											onActionClick={showConfirmationModal}
											isAllowedToDelete={isAllowedToDelete}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : (
				<NoItemFound
					onClick={() => setIsShowAddItemModal(true)}
					buttonTitle="Add Item"
					title="No items found"
					description="Add items to create your range plan"
				/>
			)}
			{isShowConfirmationModal && (
				<DeleteConfirmationModal
					title="Delete Row"
					description="Are you sure about deleting this Row?"
					onClose={() => setIsShowConfirmationModal(false)}
					onConfirm={handleRemoveRow}
				/>
			)}
			{isShowAddItemModal && (
				<AddItemModal
					onClose={() => setIsShowAddItemModal(false)}
					id={id}
					getRangePlanTableData={getRangePlanTableData}
					entityType={ENTITY_TYPE.FAVOURITE_FOLDER}
					brandId={brandId}
				/>
			)}
		</React.Fragment>
	);
};

export default RangePlanTable;
